import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useDialog } from '../contexts/DialogContext';
import LoadingSpinner from '../components/LoadingSpinner';
import CatalogoImportacionPreview from '../components/CatalogoImportacionPreview';
import CatalogoVariantesEditor from '../components/CatalogoVariantesEditor';
import './PanelControl.css';

// Componente de select con búsqueda
const SearchableSelect = ({ value, onChange, options, placeholder, disabled, allowCustom }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        // Si allowCustom está activado y hay texto escrito, usarlo como valor
        if (allowCustom && searchTerm) {
          onChange(searchTerm.toUpperCase());
        }
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchTerm, allowCustom, onChange]);

  const filteredOptions = options.filter(option =>
    String(option).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && allowCustom) {
      e.preventDefault();
      const valueToUse = searchTerm || filteredOptions[0];
      if (valueToUse) {
        onChange(typeof valueToUse === 'string' ? valueToUse.toUpperCase() : valueToUse);
        setIsOpen(false);
        setSearchTerm('');
      }
    }
  };

  const displayValue = value || placeholder;

  return (
    <div className="searchable-select" ref={wrapperRef}>
      <div 
        className={`searchable-select-input ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {isOpen ? (
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar o escribir..."
            autoFocus
            disabled={disabled}
          />
        ) : (
          <span className={!value ? 'placeholder' : ''}>{displayValue}</span>
        )}
        <span className="searchable-select-arrow">▼</span>
      </div>
      {isOpen && !disabled && (
        <div className="searchable-select-dropdown">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={index}
                className={`searchable-select-option ${option === value ? 'selected' : ''}`}
                onClick={() => handleSelect(option)}
              >
                {option || 'Todos'}
              </div>
            ))
          ) : allowCustom && searchTerm ? (
            <div 
              className="searchable-select-option custom"
              onClick={() => handleSelect(searchTerm.toUpperCase())}
            >
              ✨ Usar "{searchTerm.toUpperCase()}"
            </div>
          ) : (
            <div className="searchable-select-option disabled">No se encontraron resultados</div>
          )}
        </div>
      )}
    </div>
  );
};

const PanelControl = () => {
  const { user } = useAuth();
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const [catalogo, setCatalogo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editando, setEditando] = useState(null); // { catalogoId, grupoMadera, costos }
  const [renombrando, setRenombrando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [configuracion, setConfiguracion] = useState(null);
  const [guardandoConfig, setGuardandoConfig] = useState(false);
  const [ajusteDecoracion, setAjusteDecoracion] = useState({ proveedor: '', porcentaje: '' });
  const [previewAjusteDecoracion, setPreviewAjusteDecoracion] = useState(null);
  const [cargandoPreviewAjusteDecoracion, setCargandoPreviewAjusteDecoracion] = useState(false);
  const [aplicandoAjusteDecoracion, setAplicandoAjusteDecoracion] = useState(false);
  
  // Filtros
  const [filtros, setFiltros] = useState({
    tipo: '',
    modelo: '',
    medidas: '',
    madera: '',
    tapizado: '',
    cantidadTapizado: '',
    sku: '',
    proveedor: ''
  });

  // Paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const [itemsPorPagina, setItemsPorPagina] = useState(10);

  // Registro de stock
  const [registroStock, setRegistroStock] = useState([]);
  const [registroStockLoading, setRegistroStockLoading] = useState(false);
  const [registroStockPaginacion, setRegistroStockPaginacion] = useState({
    total: 0,
    pagina: 1,
    limite: 20,
    totalPaginas: 1
  });
  const [registroStockResumen, setRegistroStockResumen] = useState({
    ingresos: { movimientos: 0, cantidad: 0 },
    egresos: { movimientos: 0, cantidad: 0 },
    ajustes: { movimientos: 0, cantidad: 0 }
  });
  const [registroStockPagina, setRegistroStockPagina] = useState(1);
  const [filtrosRegistroStock, setFiltrosRegistroStock] = useState({
    sucursal: user?.rol === 'admin' ? '' : (user?.sucursal || ''),
    tipo: '',
    texto: '',
    desde: '',
    hasta: ''
  });

  // Catálogo seleccionado
  const [catalogoSeleccionado, setCatalogoSeleccionado] = useState('MUEBLES');

  // Modal de creación
  const [modalCrearAbierto, setModalCrearAbierto] = useState(false);
  const [modalAjusteGlobalAbierto, setModalAjusteGlobalAbierto] = useState(false);
  const [creando, setCreando] = useState(false);
  const [nuevoProducto, setNuevoProducto] = useState({
    tipo: '',
    modelo: '',
    medidas: '',
    madera: '',
    sku: '',
    proveedor: '',
    cantidadTapizado: '',
    // Para checkboxes de lustres/materiales y sus costos
    lustres: {
      sinLustre: { seleccionado: false, costo: '' },
      natural: { seleccionado: false, costo: '' },
      comun: { seleccionado: false, costo: '' },
      eco: { seleccionado: false, costo: '' },
      cuero: { seleccionado: false, costo: '' },
      tela: { seleccionado: false, costo: '' }
    },
    // Para ADICIONALES
    tipoValor: 'MONETARIO',
    valor: '',
    aplicaFactores: false,
    aceptaDecimales: false
  });

  // Notificaciones no invasivas
  const [notificacion, setNotificacion] = useState({ visible: false, mensaje: '', tipo: 'success' });

  const mostrarNotificacion = useCallback((mensaje, tipo = 'success') => {
    setNotificacion({ visible: true, mensaje, tipo });
    setTimeout(() => {
      setNotificacion({ visible: false, mensaje: '', tipo: 'success' });
    }, 3000);
  }, []);

  const cargarConfiguracion = useCallback(async () => {
    try {
      const config = await api.get('/configuracion');
      const factorDecoracionDefaults = {
        parana: 2.2,
        esperanza: 2.2,
        rosario: 2.3
      };
      const configNormalizada = config
        ? {
            ...config,
            coeficientesSucursales: Object.fromEntries(
              Object.entries(config.coeficientesSucursales || {}).map(([key, coef]) => {
                const ivaUnico = coef?.ivaTransferencia ?? coef?.ivaTarjeta ?? 1.21;
                const factorDecoracion = Number(coef?.factorDecoracion);
                return [
                  key,
                  {
                    ...coef,
                    factorDecoracion:
                      Number.isFinite(factorDecoracion) && factorDecoracion > 0
                        ? factorDecoracion
                        : (factorDecoracionDefaults[key] || factorDecoracionDefaults.parana),
                    iva: ivaUnico
                  }
                ];
              })
            )
          }
        : null;

      setConfiguracion(configNormalizada);
    } catch (error) {
      console.error('Error cargando configuración:', error);
      mostrarNotificacion('❌ Error al cargar configuración de sucursales', 'error');
    }
  }, [mostrarNotificacion]);

  const normalizarGrupo = (valor) => String(valor || '').trim().replace(/\s+/g, ' ').toUpperCase();
  const esValorFiltroReal = (valor) => {
    const v = String(valor || '').trim().toUpperCase();
    return v !== '' && v !== '-' && v !== 'NO' && v !== 'SÍ' && v !== 'SI';
  };

  const tieneCostoActivo = (valor) => Number(valor) > 0;
  const contarVariantesActivas = (variantes) => (variantes || []).filter((variante) => tieneCostoActivo(variante?.costo)).length;

  const obtenerMaderaMostrar = (grupo, categoria, tipo) => {
    const grupoTexto = String(grupo || '').trim();
    if (!grupoTexto) return '';

    const categoriaNorm = String(categoria || '').trim().toUpperCase();
    const tipoNorm = String(tipo || '').trim().toUpperCase();
    const grupoNorm = normalizarGrupo(grupoTexto);
    const prefix = `${categoriaNorm}-${tipoNorm}-`;

    // Priorizar extracción por prefijo técnico (ej: MUEBLES-TIPO-MADERA), aunque contenga '/'.
    if (grupoNorm.startsWith(prefix)) {
      return grupoNorm.slice(prefix.length);
    }

    // Productos antiguos con múltiples maderas agrupadas.
    if (grupoTexto.includes('/')) {
      return grupoTexto;
    }

    return grupoTexto;
  };

  // Cargar catálogo desde la API
  const cargarCatalogo = useCallback(async (silencioso = false) => {
    try {
      if (!silencioso) setLoading(true);
      setError(null);
      const data = await api.get('/catalogo'); // api.js ya devuelve solo los datos
      console.log('📦 Respuesta del backend:', data);
      console.log('📦 Cantidad de items:', data?.length || 0);
      setCatalogo(data || []);
    } catch (error) {
      console.error('Error cargando catálogo:', error);
      setError('Error al cargar el catálogo');
    } finally {
      if (!silencioso) setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarCatalogo();
    cargarConfiguracion();
  }, [cargarCatalogo, cargarConfiguracion]);

  const cargarRegistroStock = useCallback(async () => {
    if (catalogoSeleccionado !== 'REGISTRO_STOCK') return;

    try {
      setRegistroStockLoading(true);

      const params = new URLSearchParams({
        page: String(registroStockPagina),
        limit: '20'
      });

      if (filtrosRegistroStock.sucursal) params.append('sucursal', filtrosRegistroStock.sucursal);
      if (filtrosRegistroStock.tipo) params.append('tipo', filtrosRegistroStock.tipo);
      if (filtrosRegistroStock.texto) params.append('texto', filtrosRegistroStock.texto);
      if (filtrosRegistroStock.desde) params.append('desde', filtrosRegistroStock.desde);
      if (filtrosRegistroStock.hasta) params.append('hasta', filtrosRegistroStock.hasta);

      const response = await api.get(`/panel-control/registro-stock?${params.toString()}`);
      setRegistroStock(response?.data?.items || []);
      setRegistroStockPaginacion(response?.data?.paginacion || {
        total: 0,
        pagina: 1,
        limite: 20,
        totalPaginas: 1
      });
      setRegistroStockResumen(response?.data?.resumen || {
        ingresos: { movimientos: 0, cantidad: 0 },
        egresos: { movimientos: 0, cantidad: 0 },
        ajustes: { movimientos: 0, cantidad: 0 }
      });
    } catch (error) {
      console.error('Error cargando registro de stock:', error);
      mostrarNotificacion('❌ Error al cargar el registro de stock', 'error');
    } finally {
      setRegistroStockLoading(false);
    }
  }, [catalogoSeleccionado, filtrosRegistroStock, registroStockPagina, mostrarNotificacion]);

  useEffect(() => {
    cargarRegistroStock();
  }, [cargarRegistroStock]);

  const actualizarCoeficiente = (sucursal, campo, valor) => {
    setConfiguracion((prev) => {
      if (!prev) return prev;

      const sucursalActual = prev.coeficientesSucursales?.[sucursal] || {};
      const siguienteSucursal = {
        ...sucursalActual,
        // Mantener string mientras se edita para permitir decimales como "1.7" o "1."
        [campo]: valor
      };

      if (campo === 'iva') {
        siguienteSucursal.ivaTransferencia = valor;
        siguienteSucursal.ivaTarjeta = valor;
      }

      return {
        ...prev,
        coeficientesSucursales: {
          ...prev.coeficientesSucursales,
          [sucursal]: siguienteSucursal
        }
      };
    });
  };

  const guardarCoeficientesSucursal = async (sucursal) => {
    if (!configuracion?.coeficientesSucursales?.[sucursal]) return;

    try {
      setGuardandoConfig(true);
      const coef = configuracion.coeficientesSucursales[sucursal];
      const factorDecoracionDefaults = {
        parana: 2.2,
        esperanza: 2.2,
        rosario: 2.3
      };
      const factorDecoracion = Number(coef.factorDecoracion);
      const ivaUnico = Number(coef.iva ?? coef.ivaTransferencia ?? coef.ivaTarjeta) || 0;
      const payload = {
        contadoEfectivo: Number(coef.contadoEfectivo) || 0,
        factorDecoracion:
          Number.isFinite(factorDecoracion) && factorDecoracion > 0
            ? factorDecoracion
            : (factorDecoracionDefaults[sucursal] || factorDecoracionDefaults.parana),
        transferencia: Number(coef.transferencia) || 0,
        tarjeta: Number(coef.tarjeta) || 0,
        ivaTransferencia: ivaUnico,
        ivaTarjeta: ivaUnico,
        recargoTarjeta1: Number(coef.recargoTarjeta1) || 0,
        recargoTarjeta2: Number(coef.recargoTarjeta2) || 0
      };

      await api.put(`/configuracion/coeficientes/${sucursal}`, payload);
      mostrarNotificacion(`✅ Coeficientes de ${sucursal.toUpperCase()} actualizados`, 'success');
      await cargarConfiguracion();
    } catch (error) {
      console.error('Error guardando coeficientes:', error);
      mostrarNotificacion('❌ Error al guardar coeficientes', 'error');
    } finally {
      setGuardandoConfig(false);
    }
  };

  const renderPanelSucursales = () => {
    if (!configuracion?.coeficientesSucursales) {
      return <LoadingSpinner label="Cargando configuracion de sucursales..." inline />;
    }

    const sucursales = [
      { key: 'parana', label: 'Paraná' },
      { key: 'esperanza', label: 'Esperanza' },
      { key: 'rosario', label: 'Rosario' }
    ];

    const campos = [
      { key: 'contadoEfectivo', label: 'Factor contado' },
      { key: 'factorDecoracion', label: 'Factor decoracion' },
      { key: 'transferencia', label: 'Factor transferencia' },
      { key: 'iva', label: 'IVA' },
      { key: 'tarjeta', label: 'Factor tarjeta' },
      { key: 'recargoTarjeta1', label: 'Recargo tarjeta 1' },
      { key: 'recargoTarjeta2', label: 'Recargo tarjeta 2' }
    ];

    return (
      <div className="sucursales-config-grid">
        {sucursales.map((sucursal) => (
          <div key={sucursal.key} className="sucursal-card">
            <h3>{sucursal.label}</h3>
            <div className="sucursal-card-fields">
              {campos.map((campo) => (
                <div className="form-group" key={`${sucursal.key}-${campo.key}`}>
                  <label>{campo.label}</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={configuracion.coeficientesSucursales[sucursal.key]?.[campo.key] ?? ''}
                    onChange={(e) => {
                      const valor = e.target.value.replace(',', '.');
                      if (valor === '' || /^\d*\.?\d*$/.test(valor)) {
                        actualizarCoeficiente(sucursal.key, campo.key, valor);
                      }
                    }}
                    placeholder="0"
                  />
                </div>
              ))}
            </div>
            <button
              className="btn-guardar-sucursal"
              onClick={() => guardarCoeficientesSucursal(sucursal.key)}
              disabled={guardandoConfig}
            >
              {guardandoConfig ? 'Guardando...' : `💾 Guardar ${sucursal.label}`}
            </button>
          </div>
        ))}
      </div>
    );
  };

  const renderPanelRegistroStock = () => {
    const formatFecha = (fecha) => {
      if (!fecha) return '-';
      return new Date(fecha).toLocaleString('es-AR');
    };

    const tipoLabel = (tipo) => {
      if (tipo === 'INGRESO') return 'Ingreso';
      if (tipo === 'EGRESO') return 'Egreso';
      if (tipo === 'AJUSTE') return 'Ajuste';
      return tipo || '-';
    };

    return (
      <>
        <div className="registro-stock-toolbar">
          {user?.rol === 'admin' && (
            <div className="registro-stock-field">
              <label>Sucursal</label>
              <select
                className="registro-stock-input"
                value={filtrosRegistroStock.sucursal}
                onChange={(e) => {
                  setFiltrosRegistroStock((prev) => ({ ...prev, sucursal: e.target.value }));
                  setRegistroStockPagina(1);
                }}
              >
                <option value="">Todas las sucursales</option>
                <option value="parana">Parana</option>
                <option value="esperanza">Esperanza</option>
                <option value="rosario">Rosario</option>
              </select>
            </div>
          )}

          <div className="registro-stock-field">
            <label>Tipo de movimiento</label>
            <select
              className="registro-stock-input"
              value={filtrosRegistroStock.tipo}
              onChange={(e) => {
                setFiltrosRegistroStock((prev) => ({ ...prev, tipo: e.target.value }));
                setRegistroStockPagina(1);
              }}
            >
              <option value="">Todos los tipos</option>
              <option value="INGRESO">Ingresos</option>
              <option value="EGRESO">Egresos</option>
              <option value="AJUSTE">Ajustes</option>
            </select>
          </div>

          <div className="registro-stock-field">
            <label>Fecha del movimiento desde</label>
            <input
              className="registro-stock-input"
              type="date"
              value={filtrosRegistroStock.desde}
              onChange={(e) => {
                setFiltrosRegistroStock((prev) => ({ ...prev, desde: e.target.value }));
                setRegistroStockPagina(1);
              }}
            />
          </div>

          <div className="registro-stock-field">
            <label>Fecha del movimiento hasta</label>
            <input
              className="registro-stock-input"
              type="date"
              value={filtrosRegistroStock.hasta}
              onChange={(e) => {
                setFiltrosRegistroStock((prev) => ({ ...prev, hasta: e.target.value }));
                setRegistroStockPagina(1);
              }}
            />
          </div>

          <div className="registro-stock-field registro-stock-input-busqueda">
            <label>Buscar</label>
            <input
              className="registro-stock-input"
              type="text"
              placeholder="Modelo, SKU, proveedor, usuario..."
              value={filtrosRegistroStock.texto}
              onChange={(e) => {
                setFiltrosRegistroStock((prev) => ({ ...prev, texto: e.target.value }));
                setRegistroStockPagina(1);
              }}
            />
          </div>
        </div>

        <div className="registro-stock-resumen-grid">
          <div className="registro-stock-resumen-card ingreso">
            <h4>Ingresos</h4>
            <p>{registroStockResumen.ingresos.cantidad}</p>
            <span>{registroStockResumen.ingresos.movimientos} movimientos</span>
          </div>
          <div className="registro-stock-resumen-card egreso">
            <h4>Egresos</h4>
            <p>{registroStockResumen.egresos.cantidad}</p>
            <span>{registroStockResumen.egresos.movimientos} movimientos</span>
          </div>
          <div className="registro-stock-resumen-card ajuste">
            <h4>Ajustes</h4>
            <p>{registroStockResumen.ajustes.cantidad}</p>
            <span>{registroStockResumen.ajustes.movimientos} movimientos</span>
          </div>
        </div>

        {registroStockLoading ? (
          <LoadingSpinner label="Cargando registro de stock..." inline />
        ) : (
          <>
            <div className="panel-tabla">
              <table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Sucursal</th>
                    <th>Producto</th>
                    <th>Variante</th>
                    <th>Cant.</th>
                    <th>Antes</th>
                    <th>Despues</th>
                    <th>Origen</th>
                    <th>Usuario</th>
                  </tr>
                </thead>
                <tbody>
                  {registroStock.length === 0 ? (
                    <tr>
                      <td className="text-center" colSpan="10">
                        No hay movimientos para los filtros seleccionados
                      </td>
                    </tr>
                  ) : (
                    registroStock.map((mov) => (
                      <tr key={mov._id}>
                        <td>{formatFecha(mov.fecha)}</td>
                        <td>
                          <span className={`registro-stock-tipo ${String(mov.tipoMovimiento || '').toLowerCase()}`}>
                            {tipoLabel(mov.tipoMovimiento)}
                          </span>
                        </td>
                        <td>{String(mov.sucursal || '-').toUpperCase()}</td>
                        <td>
                          {mov?.producto?.modelo || '-'}
                          <br />
                          <small>{mov?.producto?.categoria || '-'}</small>
                        </td>
                        <td>
                          {mov?.producto?.variante?.sku || mov?.producto?.variante?.madera || '-'}
                          <br />
                          <small>{mov?.producto?.variante?.proveedor || mov?.producto?.variante?.lustre || '-'}</small>
                        </td>
                        <td>{mov.cantidad}</td>
                        <td>{mov.stockAnterior ?? '-'}</td>
                        <td>{mov.stockPosterior ?? '-'}</td>
                        <td>{mov.origen || '-'}</td>
                        <td>{mov?.usuario?.username || 'sistema'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="registro-stock-paginacion">
              <button
                className="btn-paginacion"
                onClick={() => setRegistroStockPagina((prev) => Math.max(1, prev - 1))}
                disabled={registroStockPaginacion.pagina <= 1}
              >
                Anterior
              </button>
              <span>
                Pagina {registroStockPaginacion.pagina} de {registroStockPaginacion.totalPaginas} ({registroStockPaginacion.total} movimientos)
              </span>
              <button
                className="btn-paginacion"
                onClick={() => setRegistroStockPagina((prev) => Math.min(registroStockPaginacion.totalPaginas, prev + 1))}
                disabled={registroStockPaginacion.pagina >= registroStockPaginacion.totalPaginas}
              >
                Siguiente
              </button>
            </div>
          </>
        )}
      </>
    );
  };

  // Limpiar filtros y formulario al cambiar de tab
  useEffect(() => {
    setFiltros({
      tipo: '',
      modelo: '',
      medidas: '',
      madera: '',
      tapizado: '',
      cantidadTapizado: '',
      sku: '',
      proveedor: ''
    });
    setPaginaActual(1);
    
    // Si el modal está abierto, resetear el formulario al cambiar de categoría
    if (modalCrearAbierto) {
      setNuevoProducto({
        tipo: '',
        modelo: '',
        medidas: '',
        madera: '',
        tapizado: '',
        sku: '',
        proveedor: '',
        cantidadTapizado: '',
        lustres: {
          sinLustre: { seleccionado: false, costo: '' },
          natural: { seleccionado: false, costo: '' },
          comun: { seleccionado: false, costo: '' },
          eco: { seleccionado: false, costo: '' },
          cuero: { seleccionado: false, costo: '' },
          tela: { seleccionado: false, costo: '' }
        },
        tipoValor: 'MONETARIO',
        valor: '',
        aplicaFactores: false,
        aceptaDecimales: false
      });
    }

    if (catalogoSeleccionado !== 'DECORACION') {
      setModalAjusteGlobalAbierto(false);
      setPreviewAjusteDecoracion(null);
    }
  }, [catalogoSeleccionado, modalCrearAbierto]);

  //Agrupar variantes por grupoActualizacion para mostrar como el Excel
  const agruparCatalogo = () => {
    const grupos = [];
    
    // Filtrar por categoría seleccionada
    const catalogoFiltrado = catalogo.filter(item => item.categoria === catalogoSeleccionado);
    
    catalogoFiltrado.forEach(catalogoItem => {
      if (!catalogoItem.variantes || catalogoItem.variantes.length === 0) return;
      
      // Para DECORACION, lógica simple: cada variante es una fila
      if (catalogoItem.categoria === 'DECORACION') {
        catalogoItem.variantes.forEach(variante => {
          grupos.push({
            catalogoId: catalogoItem._id,
            categoria: catalogoItem.categoria,
            tipo: catalogoItem.tipo,
            modelo: catalogoItem.modelo,
            sku: variante.sku,
            proveedor: variante.proveedor,
            comun: variante.costo
          });
        });
      } else if (catalogoItem.categoria === 'ADICIONALES') {
        // Para ADICIONALES, lógica simple: cada variante es una fila
        catalogoItem.variantes.forEach(variante => {
          grupos.push({
            catalogoId: catalogoItem._id,
            categoria: catalogoItem.categoria,
            tipo: catalogoItem.tipo,
            modelo: catalogoItem.modelo,
            tipoValor: variante.tipoValor,
            comun: variante.costo, // Usar 'comun' para compatibilidad con la tabla
            aplicaFactores: variante.aplicaFactores,
            aceptaDecimales: !!variante.aceptaDecimales
          });
        });
      } else if (catalogoItem.categoria === 'SOFA') {
        const variantesAgrupadas = {};
        
        catalogoItem.variantes.forEach(variante => {
          const subGrupo = `${variante.medidas}|${variante.cantidadTapizado}`;
          
          if (!variantesAgrupadas[subGrupo]) {
            variantesAgrupadas[subGrupo] = {
              eco: null,
              cuero: null,
              tela: null,
              medidas: variante.medidas,
              varianteIds: [],
              cantidadTapizado: variante.cantidadTapizado || '-'
            };
          }
          
          variantesAgrupadas[subGrupo].varianteIds.push(variante._id);
          // Mapear lustres a columnas específicas de sofá
          if (variante.lustre === 'ECO') variantesAgrupadas[subGrupo].eco = variante.costo;
          if (variante.lustre === 'CUERO') variantesAgrupadas[subGrupo].cuero = variante.costo;
          if (variante.lustre === 'TELA') variantesAgrupadas[subGrupo].tela = variante.costo;
        });
        
        Object.keys(variantesAgrupadas).forEach(grupo => {
          const varianteData = variantesAgrupadas[grupo];
          grupos.push({
            catalogoId: catalogoItem._id,
            categoria: catalogoItem.categoria,
            tipo: catalogoItem.tipo,
            modelo: catalogoItem.modelo,
            medidas: varianteData.medidas,
            cantidadTapizado: varianteData.cantidadTapizado,
            varianteIds: varianteData.varianteIds,
            eco: varianteData.eco,
            cuero: varianteData.cuero,
            tela: varianteData.tela
          });
        });
      } else {
        // Lógica para MUEBLES, RESPALDO, SILLA
        const variantesAgrupadas = {};
        
        catalogoItem.variantes.forEach(variante => {
          const grupo = variante.grupoActualizacion || variante.madera;
          const maderaMostrar = obtenerMaderaMostrar(grupo, catalogoItem.categoria, catalogoItem.tipo);
          const tapizadoMostrar = variante.tapizado || (catalogoItem.requireTapizado ? 'SÍ' : 'NO');
          const cantidadMostrar = variante.cantidadTapizado || '-';
          const subGrupo = `${normalizarGrupo(maderaMostrar)}|${variante.medidas || ''}|${normalizarGrupo(tapizadoMostrar)}|${normalizarGrupo(cantidadMostrar)}`;
          
          if (!variantesAgrupadas[subGrupo]) {
            // Determinar qué mostrar en la columna MADERA:
            // - Si grupoActualizacion tiene "/" → múltiples maderas agrupadas (ej: "GUATAMBU / PARAISO")
            // - Si grupoActualizacion tiene formato "CAT-TIPO-MADERA" → extraer solo la madera
            // - Si no, usar grupoActualizacion tal cual
            variantesAgrupadas[subGrupo] = {
              grupoMadera: grupo, // Para identificar el grupo (grupoActualizacion completo)
              varianteIds: [],
              maderaMostrar: maderaMostrar, // Para mostrar en la tabla
              sin_lustre: null,
              natural: null,
              comun: null,
              medidas: variante.medidas,
              tapizado: tapizadoMostrar,
              cantidadTapizado: cantidadMostrar
            };
          }
          variantesAgrupadas[subGrupo].varianteIds.push(variante._id);
          // Mapear nombres de lustre a columnas
          const nombreColumna = variante.lustre === 'SIN LUSTRE' ? 'sin_lustre' : 
                               variante.lustre === 'NATURAL' ? 'natural' : 'comun';
          variantesAgrupadas[subGrupo][nombreColumna] = variante.costo;
        });
        
        // Crear filas para la tabla
        Object.keys(variantesAgrupadas).forEach(grupo => {
          const varianteData = variantesAgrupadas[grupo];
          grupos.push({
            catalogoId: catalogoItem._id,
            categoria: catalogoItem.categoria,
            tipo: catalogoItem.tipo,
            modelo: catalogoItem.modelo,
            medidas: varianteData.medidas,
            madera: varianteData.maderaMostrar, // Mostrar según el formato del grupo
            grupoMadera: varianteData.grupoMadera, // Mantener el grupo completo para operaciones
            varianteIds: varianteData.varianteIds,
            tapizado: varianteData.tapizado,
            cantidadTapizado: varianteData.cantidadTapizado,
            sin_lustre: varianteData.sin_lustre,
            natural: varianteData.natural,
            comun: varianteData.comun
          });
        });
      }
    });
    
    return grupos;
  };

  const handleEditarNombre = async grupo => {
    if (renombrando) return;
    const respuesta = await showPrompt('Nombre del producto', { title: 'Editar nombre del catalogo', defaultValue: grupo.modelo,
      placeholder: 'Nombre del modelo', confirmLabel: 'Continuar' });
    if (respuesta === null) return;
    const nombre = String(respuesta).trim().replace(/\s+/g, ' ').toUpperCase();
    if (!nombre || nombre.length > 120) {
      await showAlert('Indique un nombre de entre 1 y 120 caracteres.', { type: 'warning' });
      return;
    }
    if (nombre === grupo.modelo) return;
    const confirmado = await showConfirm(`Cambiar ${grupo.modelo} por ${nombre} en todas sus variantes del tipo ${grupo.tipo}. Tambien se actualizaran stock, presupuestos, pedidos y registros asociados, incluidos los guardados. Los importes no cambiaran.`,
      { title: 'Confirmar cambio de nombre', confirmLabel: 'Cambiar nombre' });
    if (!confirmado) return;
    setRenombrando(true);
    try {
      await api.put(`/catalogo/${grupo.catalogoId}/nombre`, { nombre, nombreActual: grupo.modelo, confirmar: true });
      setFiltros(actual => ({ ...actual, modelo: actual.modelo === grupo.modelo ? nombre : actual.modelo }));
      await cargarCatalogo(true);
      await showAlert('Nombre actualizado en el catalogo y sus referencias.', { type: 'success' });
    } catch (error) {
      await showAlert(error.message || 'No se pudo cambiar el nombre del producto.', { type: 'error' });
    } finally { setRenombrando(false); }
  };

  const handleEditarCosto = (grupo) => {
    if (!['ADICIONALES', 'DECORACION'].includes(grupo.categoria)) {
      const documento = catalogo.find(item => item._id === grupo.catalogoId);
      setEditando({ ...grupo, modo: 'VARIANTES', variantes: documento.variantes.filter(variante => grupo.varianteIds.includes(variante._id)) });
      return;
    }
    // Para ADICIONALES, la estructura es diferente
    if (grupo.categoria === 'ADICIONALES') {
      setEditando({
        catalogoId: grupo.catalogoId,
        tipo: grupo.tipo,
        modelo: grupo.modelo,
        categoria: grupo.categoria,
        tipoValor: grupo.tipoValor || 'MONETARIO',
        aplicaFactores: grupo.aplicaFactores || false,
        aceptaDecimales: grupo.aceptaDecimales || false,
        costos: {
          comun: grupo.comun || 0
        }
      });
    } else {
      setEditando({
        catalogoId: grupo.catalogoId,
        tipo: grupo.tipo,
        modelo: grupo.modelo,
        categoria: grupo.categoria,
        medidas: grupo.medidas,
        madera: grupo.madera,
        grupoMadera: grupo.grupoMadera, // ✅ Usar grupoMadera (grupoActualizacion completo) no madera
        tapizado: grupo.tapizado,
        cantidadTapizado: grupo.cantidadTapizado,
        sku: grupo.sku,
        proveedor: grupo.proveedor,
        costos: {
          sin_lustre: grupo.sin_lustre || 0,
          natural: grupo.natural || 0,
          comun: grupo.comun || 0
        }
      });
    }
  };

  const handleGuardarCostos = async () => {
    try {
      setGuardando(true);
      
      // Para ADICIONALES, la estructura es diferente
      if (editando.categoria === 'ADICIONALES') {
        const costoValor = editando.costos.comun === '' ? 0 : parseInt(editando.costos.comun) || 0;
        
        const data = await api.put(`/catalogo/${editando.catalogoId}/actualizar-costos`, {
          categoria: 'ADICIONALES',
          modelo: editando.modelo,
          tipoValor: editando.tipoValor,
          costo: costoValor,
          aplicaFactores: editando.aplicaFactores,
          aceptaDecimales: editando.aceptaDecimales
        });
        
        // Actualizar solo el item modificado en el estado local (sin recargar todo)
        setCatalogo(prevCatalogo => 
          prevCatalogo.map(item => 
            item._id === editando.catalogoId ? data.catalogoItem : item
          )
        );
        
        setEditando(null);
        mostrarNotificacion('✅ Adicional actualizado correctamente', 'success');
      } else {
        // Para otras categorías, usar la lógica original
        const costosParaGuardar = {
          sin_lustre: editando.costos.sin_lustre === '' ? 0 : parseInt(editando.costos.sin_lustre) || 0,
          natural: editando.costos.natural === '' ? 0 : parseInt(editando.costos.natural) || 0,
          comun: editando.costos.comun === '' ? 0 : parseInt(editando.costos.comun) || 0
        };
        
        const data = await api.put(`/catalogo/${editando.catalogoId}/actualizar-costos`, {
          grupoMadera: editando.grupoMadera,
          costos: costosParaGuardar,
          categoria: editando.categoria,
          medidas: editando.medidas,
          tapizado: editando.tapizado,
          cantidadTapizado: editando.cantidadTapizado,
          sku: editando.sku,
          proveedor: editando.proveedor
        });
        
        // Actualizar solo el item modificado en el estado local (sin recargar todo)
        setCatalogo(prevCatalogo => 
          prevCatalogo.map(item => 
            item._id === editando.catalogoId ? data.catalogoItem : item
          )
        );
        
        setEditando(null);
        mostrarNotificacion('✅ Costos actualizados correctamente', 'success');
      }

    } catch (error) {
      console.error('Error guardando costos:', error);
      mostrarNotificacion('❌ Error al guardar los costos', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const handleCancelar = () => {
    setEditando(null);
  };

  // Funciones para modal de creación
  const abrirModalCrear = () => {
    setNuevoProducto({
      tipo: '',
      modelo: '',
      medidas: '',
      madera: '',
      tapizado: '',
      sku: '',
      proveedor: '',
      cantidadTapizado: '',
      tipoValor: 'MONETARIO',
      valor: '',
      aplicaFactores: false,
      aceptaDecimales: false,
      lustres: {
        sinLustre: { seleccionado: false, costo: '' },
        natural: { seleccionado: false, costo: '' },
        comun: { seleccionado: false, costo: '' },
        eco: { seleccionado: false, costo: '' },
        cuero: { seleccionado: false, costo: '' },
        tela: { seleccionado: false, costo: '' }
      }
    });
    setModalCrearAbierto(true);
  };

  const cerrarModalCrear = () => {
    setModalCrearAbierto(false);
    setNuevoProducto({
      tipo: '',
      modelo: '',
      medidas: '',
      madera: '',
      tapizado: '',
      sku: '',
      proveedor: '',
      cantidadTapizado: '',
      tipoValor: 'MONETARIO',
      valor: '',
      aplicaFactores: false,
      aceptaDecimales: false,
      lustres: {
        sinLustre: { seleccionado: false, costo: '' },
        natural: { seleccionado: false, costo: '' },
        comun: { seleccionado: false, costo: '' },
        eco: { seleccionado: false, costo: '' },
        cuero: { seleccionado: false, costo: '' },
        tela: { seleccionado: false, costo: '' }
      }
    });
  };

  const handleCrearProducto = async () => {
    try {
      // Validaciones básicas
      if (catalogoSeleccionado === 'DECORACION') {
        if (!nuevoProducto.modelo || !nuevoProducto.proveedor) {
          await showAlert('Por favor complete todos los campos obligatorios', { type: 'warning', title: 'Datos incompletos' });
          return;
        }
        if (!nuevoProducto.lustres.sinLustre.costo) {
          await showAlert('Por favor ingrese el costo', { type: 'warning', title: 'Dato faltante' });
          return;
        }
      } else if (catalogoSeleccionado === 'ADICIONALES') {
        if (!nuevoProducto.modelo) {
          await showAlert('Por favor ingrese el nombre del adicional', { type: 'warning', title: 'Dato faltante' });
          return;
        }
        if (!nuevoProducto.valor) {
          await showAlert('Por favor ingrese el valor', { type: 'warning', title: 'Dato faltante' });
          return;
        }
      } else {
        if (!nuevoProducto.tipo || !nuevoProducto.modelo) {
          await showAlert('Por favor complete el tipo y modelo', { type: 'warning', title: 'Datos incompletos' });
          return;
        }
        if (catalogoSeleccionado !== 'SILLA' && !nuevoProducto.medidas) {
          await showAlert('Por favor ingrese las medidas', { type: 'warning', title: 'Dato faltante' });
          return;
        }
        if (catalogoSeleccionado !== 'SOFA' && !nuevoProducto.madera) {
          await showAlert('Por favor ingrese la madera', { type: 'warning', title: 'Dato faltante' });
          return;
        }
        if (catalogoSeleccionado === 'SOFA' && !nuevoProducto.cantidadTapizado) {
          await showAlert('Por favor ingrese la cantidad de tela/cuero', { type: 'warning', title: 'Dato faltante' });
          return;
        }

        // Validar que haya al menos un lustre/material seleccionado
        const lustresSeleccionados = Object.entries(nuevoProducto.lustres).filter(
          ([, data]) => data.seleccionado && data.costo
        );
        if (lustresSeleccionados.length === 0) {
          await showAlert('Por favor seleccione al menos un lustre/material con su costo', { type: 'warning', title: 'Dato faltante' });
          return;
        }
      }

      setCreando(true);

      // Construir el objeto para enviar al backend
      let datosProducto;

      if (catalogoSeleccionado === 'DECORACION') {
        datosProducto = {
          categoria: catalogoSeleccionado,
          tipo: 'DECORACION',
          modelo: nuevoProducto.modelo,
          variantes: [{
            sku: nuevoProducto.sku.trim() || '', // Si está vacío, el backend lo auto-genera
            proveedor: nuevoProducto.proveedor,
            costo: parseInt(nuevoProducto.lustres.sinLustre.costo) || 0
          }]
        };
      } else if (catalogoSeleccionado === 'ADICIONALES') {
        datosProducto = {
          categoria: catalogoSeleccionado,
          tipo: nuevoProducto.modelo, // Para ADICIONALES, tipo = modelo
          modelo: nuevoProducto.modelo,
          variantes: [{
            costo: parseInt(nuevoProducto.valor) || 0,
            tipoValor: nuevoProducto.tipoValor,
            aplicaFactores: nuevoProducto.aplicaFactores,
            aceptaDecimales: nuevoProducto.aceptaDecimales
          }]
        };
      } else {
        // Para MUEBLES, SILLA, SOFA, RESPALDO
        const variantes = [];
        const lustresMap = {
          sinLustre: 'SIN LUSTRE',
          natural: 'NATURAL',
          comun: 'COMUN',
          eco: 'ECO',
          cuero: 'CUERO',
          tela: 'TELA'
        };

        Object.entries(nuevoProducto.lustres).forEach(([key, data]) => {
          if (data.seleccionado && data.costo) {
            const variante = {
              costo: parseInt(data.costo) || 0,
              lustre: lustresMap[key]
            };

            if (catalogoSeleccionado !== 'SOFA') {
              variante.madera = nuevoProducto.madera;
            }
            if (catalogoSeleccionado !== 'SILLA') {
              variante.medidas = nuevoProducto.medidas;
            }
            if (catalogoSeleccionado === 'SOFA') {
              variante.cantidadTapizado = nuevoProducto.cantidadTapizado;
            }
            if (catalogoSeleccionado === 'RESPALDO' || catalogoSeleccionado === 'SILLA') {
              variante.tapizado = nuevoProducto.tapizado || '';
              if (nuevoProducto.cantidadTapizado) {
                variante.cantidadTapizado = nuevoProducto.cantidadTapizado;
              }
            }

            variantes.push(variante);
          }
        });

        datosProducto = {
          categoria: catalogoSeleccionado,
          tipo: nuevoProducto.tipo,
          modelo: nuevoProducto.modelo,
          requireTapizado: (catalogoSeleccionado === 'RESPALDO' || catalogoSeleccionado === 'SILLA') && !!nuevoProducto.tapizado,
          variantes
        };
      }

      console.log('📤 Enviando producto al backend:', datosProducto);
      console.log('📋 Variantes detalladas:', JSON.stringify(datosProducto.variantes, null, 2));

      const respuesta = await api.post('/catalogo', datosProducto);
      
      console.log('✅ Producto creado:', respuesta);
      
      // Si el backend devolvió un item existente actualizado, reemplazarlo; si no, agregarlo.
      setCatalogo((prevCatalogo) => {
        const index = prevCatalogo.findIndex((item) => item._id === respuesta._id);
        if (index === -1) {
          return [...prevCatalogo, respuesta];
        }
        const actualizado = [...prevCatalogo];
        actualizado[index] = respuesta;
        return actualizado;
      });
      
      cerrarModalCrear();
      mostrarNotificacion(`✅ Producto "${nuevoProducto.modelo}" creado exitosamente`, 'success');
    } catch (error) {
      console.error('❌ Error al crear producto:', error);
      const errorMessage = typeof error === 'string'
        ? error
        : error?.message || error?.response?.data?.message || '❌ Error al crear el producto';
      mostrarNotificacion(errorMessage, 'error');
    } finally {
      setCreando(false);
    }
  };

  const handleEliminarProducto = async (catalogoId, tipo, modelo, grupoMadera, tapizado, cantidadTapizado, medidas, categoria, sku, proveedor) => {
    // Contar cuántas variantes tiene este grupo
    const catalogoItem = catalogo.find(item => item._id === catalogoId);
    if (!catalogoItem) return;
    
    const tapizadoFiltro = esValorFiltroReal(tapizado) ? tapizado : '';
    const cantidadTapizadoFiltro = esValorFiltroReal(cantidadTapizado) ? cantidadTapizado : '';

    const variantesGrupo = catalogoItem.variantes.filter(v => {
      // Para ADICIONALES, no hay filtrado (solo hay una variante por producto)
      if (categoria === 'ADICIONALES') {
        return true; // Eliminar todas las variantes (que es solo 1)
      }
      
      // Para DECORACION, filtrar por SKU
      if (categoria === 'DECORACION') {
        return v.sku === sku;
      }
      
      // Para SOFA, filtrar solo por medidas y cantidadTapizado
      if (categoria === 'SOFA') {
        if (medidas && v.medidas !== medidas) return false;
        if (cantidadTapizado && v.cantidadTapizado !== cantidadTapizado) return false;
        return true;
      }
      
      // Para otros (MUEBLES, RESPALDO, SILLA), filtrar por madera, tapizado y cantidad
      if (normalizarGrupo(v.grupoActualizacion || v.madera) !== normalizarGrupo(grupoMadera)) return false;
      if (tapizadoFiltro && v.tapizado !== tapizadoFiltro) return false;
      if (cantidadTapizadoFiltro && v.cantidadTapizado !== cantidadTapizadoFiltro) return false;
      return true;
    });
    
    const totalVariantes = contarVariantesActivas(catalogoItem.variantes);
    const variantesGrupoActivas = contarVariantesActivas(variantesGrupo);
    const variantesRestantes = Math.max(0, totalVariantes - variantesGrupoActivas);
    
    const mensajeDescripcion = categoria === 'ADICIONALES'
      ? `"${modelo}"`
      : categoria === 'DECORACION'
      ? `"${modelo}" (SKU: ${sku}, Proveedor: ${proveedor})`
      : categoria === 'SOFA' 
      ? `"${tipo} - ${modelo}" (${medidas}, ${cantidadTapizado})`
      : `las variantes de "${grupoMadera}" del producto "${tipo} - ${modelo}"`;
    
    const mensaje = variantesRestantes > 0
      ? `¿Está seguro de eliminar ${mensajeDescripcion}?\n\n` +
        `Se eliminarán ${variantesGrupoActivas} variantes activas de este grupo.\n` +
        `Quedarán ${variantesRestantes} variantes restantes.\n\n` +
        `Esta acción no se puede deshacer.`
      : `¿Está seguro de eliminar ${mensajeDescripcion} del catálogo?\n\n` +
        `${categoria === 'ADICIONALES' ? 'Se eliminará el adicional completo del catálogo.' : `Este es el último grupo de variantes activas (${variantesGrupoActivas}).\nSe eliminará el producto completo del catálogo.`}\n\n` +
        `Esta acción no se puede deshacer.`;
    
    const confirmado = await showConfirm(mensaje, {
      title: 'Eliminar del catálogo',
      confirmLabel: 'Eliminar',
    });
    if (!confirmado) return;

    try {
      const datosEliminar = { 
        grupoMadera: grupoMadera || '-',
        tapizado: tapizadoFiltro,
        cantidadTapizado: cantidadTapizadoFiltro,
        medidas,
        categoria,
        sku,
        proveedor
      };

      console.log('=== ELIMINAR PRODUCTO ===');
      console.log('Datos a enviar:', datosEliminar);

      const result = await api.delete(`/catalogo/${catalogoId}/eliminar-variantes`, {
        data: datosEliminar
      });
      
      // Actualizar el estado localmente sin recargar
      if (result.itemEliminado) {
        // Eliminar el item completo
        setCatalogo(prevCatalogo => prevCatalogo.filter(item => item._id !== catalogoId));
        mostrarNotificacion(`✅ Producto eliminado completamente (${result.variantesEliminadas} variantes)`, 'success');
      } else {
        // Actualizar solo el item modificado
        await cargarCatalogo(); // En este caso sí necesitamos recargar para obtener las variantes actualizadas
        mostrarNotificacion(`✅ ${result.variantesEliminadas} variante(s) eliminada(s). Quedan ${result.variantesRestantes}`, 'success');
      }
      
      setPaginaActual(1);
    } catch (error) {
      console.error('Error eliminando variantes:', error);
      mostrarNotificacion(error?.message || 'Error al eliminar las variantes del catalogo', 'error');
    }
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
    setPaginaActual(1); // Reset a la primera página cuando se cambian filtros
  };

  const handleAjusteDecoracionChange = (campo, valor) => {
    setAjusteDecoracion((prev) => ({ ...prev, [campo]: valor }));
    setPreviewAjusteDecoracion(null);
  };

  const handlePorcentajeAjusteChange = (valor) => {
    const valorNormalizado = valor.replace(',', '.');
    if (valorNormalizado === '' || /^-?\d*\.?\d*$/.test(valorNormalizado)) {
      handleAjusteDecoracionChange('porcentaje', valorNormalizado);
    }
  };

  const validarAjusteDecoracion = async () => {
    const proveedor = String(ajusteDecoracion.proveedor || '').trim();
    const porcentajeTexto = String(ajusteDecoracion.porcentaje || '').trim();
    const porcentajeNumero = Number(porcentajeTexto);

    if (!proveedor) {
      await showAlert('Seleccioná un proveedor para continuar.', { type: 'warning', title: 'Dato faltante' });
      return null;
    }

    if (porcentajeTexto === '' || !Number.isFinite(porcentajeNumero)) {
      await showAlert('Ingresá un porcentaje válido.', { type: 'warning', title: 'Dato inválido' });
      return null;
    }

    if (porcentajeNumero === 0) {
      await showAlert('El porcentaje no puede ser 0.', { type: 'warning', title: 'Dato inválido' });
      return null;
    }

    return { proveedor, porcentaje: porcentajeNumero };
  };

  const handlePreviewAjusteDecoracion = async () => {
    const datosValidados = await validarAjusteDecoracion();
    if (!datosValidados) return;

    try {
      setCargandoPreviewAjusteDecoracion(true);
      const preview = await api.post('/catalogo/decoracion/ajuste-proveedor/preview', datosValidados);
      setPreviewAjusteDecoracion(preview);
      mostrarNotificacion(`🔎 Vista previa lista: ${preview.totalVariantes} decoraciones afectadas`, 'success');
    } catch (error) {
      console.error('Error generando preview de ajuste:', error);
      setPreviewAjusteDecoracion(null);
      await showAlert(error.message || 'No se pudo generar la vista previa', { type: 'error', title: 'Error' });
    } finally {
      setCargandoPreviewAjusteDecoracion(false);
    }
  };

  const handleAplicarAjusteDecoracion = async () => {
    const datosValidados = await validarAjusteDecoracion();
    if (!datosValidados) return;

    const porcentajeLabel = `${datosValidados.porcentaje > 0 ? '+' : ''}${datosValidados.porcentaje}%`;
    const confirmado = await showConfirm(
      `Se aplicará un ajuste de ${porcentajeLabel} a todas las decoraciones del proveedor "${datosValidados.proveedor}".\n\nEsta acción actualizará los costos en el catálogo.`,
      {
        title: 'Confirmar ajuste masivo',
        confirmLabel: 'Aplicar ajuste'
      }
    );
    if (!confirmado) return;

    try {
      setAplicandoAjusteDecoracion(true);
      const resultado = await api.post('/catalogo/decoracion/ajuste-proveedor/aplicar', datosValidados);
      await cargarCatalogo();
      setPreviewAjusteDecoracion(null);
      mostrarNotificacion(`✅ Ajuste aplicado en ${resultado.variantesAfectadas} decoraciones`, 'success');
    } catch (error) {
      console.error('Error aplicando ajuste masivo:', error);
      await showAlert(error.message || 'No se pudo aplicar el ajuste', { type: 'error', title: 'Error' });
    } finally {
      setAplicandoAjusteDecoracion(false);
    }
  };

  const aplicarFiltros = (grupos) => {
    const normalizeFilterValue = (valor) => String(valor || '').trim().toUpperCase();

    return grupos.filter(grupo => {
      if (filtros.tipo && normalizeFilterValue(filtros.tipo) !== normalizeFilterValue(grupo.tipo)) return false;
      if (filtros.modelo && normalizeFilterValue(filtros.modelo) !== normalizeFilterValue(grupo.modelo)) return false;
      if (filtros.medidas && normalizeFilterValue(filtros.medidas) !== normalizeFilterValue(grupo.medidas)) return false;
      if (filtros.madera && normalizeFilterValue(filtros.madera) !== normalizeFilterValue(grupo.madera)) return false;
      if (filtros.tapizado && normalizeFilterValue(filtros.tapizado) !== normalizeFilterValue(grupo.tapizado)) return false;
      if (filtros.cantidadTapizado && normalizeFilterValue(filtros.cantidadTapizado) !== normalizeFilterValue(grupo.cantidadTapizado)) return false;
      if (filtros.sku && normalizeFilterValue(filtros.sku) !== normalizeFilterValue(grupo.sku)) return false;
      if (filtros.proveedor && normalizeFilterValue(filtros.proveedor) !== normalizeFilterValue(grupo.proveedor)) return false;
      return true;
    });
  };

  const obtenerUnicosCaseInsensitive = (valores) => {
    const mapa = new Map();
    valores
      .filter(Boolean)
      .map((valor) => String(valor).trim())
      .filter(Boolean)
      .forEach((valor) => {
        const clave = valor.toUpperCase();
        if (!mapa.has(clave)) {
          mapa.set(clave, valor);
        }
      });
    return Array.from(mapa.values());
  };

  // Obtener opciones únicas para cada filtro basadas en grupos filtrados
  const obtenerOpcionesUnicas = (gruposBase, gruposFiltrados) => {
    const ordenarOpciones = (valores) => valores.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));

    return {
      tipos: ordenarOpciones(obtenerUnicosCaseInsensitive(gruposFiltrados.map(g => g.tipo))),
      modelos: ordenarOpciones(obtenerUnicosCaseInsensitive(gruposFiltrados.map(g => g.modelo))),
      medidas: ordenarOpciones(obtenerUnicosCaseInsensitive(gruposFiltrados.map(g => g.medidas))),
      maderas: ordenarOpciones(obtenerUnicosCaseInsensitive(gruposFiltrados.map(g => g.madera))),
      tapizados: ordenarOpciones(obtenerUnicosCaseInsensitive(gruposFiltrados.map(g => g.tapizado))),
      cantidadesTapizado: ordenarOpciones(obtenerUnicosCaseInsensitive(gruposFiltrados.map(g => g.cantidadTapizado))),
      skus: ordenarOpciones(obtenerUnicosCaseInsensitive(gruposFiltrados.map(g => g.sku))),
      proveedores: ordenarOpciones(obtenerUnicosCaseInsensitive(gruposFiltrados.map(g => g.proveedor)))
    };
  };

  if (loading) {
    return <LoadingSpinner label="Cargando panel de control..." />;
  }

  if (user?.rol !== 'admin') {
    return <div className="error">Acceso denegado. Solo administradores pueden ingresar al Panel de Control.</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  const catalogoAgrupado = agruparCatalogo();
  const catalogoFiltrado = aplicarFiltros(catalogoAgrupado);
  const opciones = obtenerOpcionesUnicas(catalogoAgrupado, catalogoFiltrado);
  const opcionesCrear = obtenerOpcionesUnicas(catalogoAgrupado, catalogoAgrupado);
  const proveedoresDecoracion = obtenerUnicosCaseInsensitive(
    catalogo
      .filter(item => item.categoria === 'DECORACION')
      .flatMap(item => (item.variantes || []).map(variante => variante.proveedor))
  ).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));

  // Cálculos de paginación
  const totalItems = catalogoFiltrado.length;
  const totalPaginas = Math.ceil(totalItems / itemsPorPagina);
  const indiceInicio = (paginaActual - 1) * itemsPorPagina;
  const indiceFin = indiceInicio + itemsPorPagina;
  const catalogoPaginado = catalogoFiltrado.slice(indiceInicio, indiceFin);

  const irAPagina = (pagina) => {
    if (pagina >= 1 && pagina <= totalPaginas) {
      setPaginaActual(pagina);
    }
  };

  return (
    <div className="panel-control-container">
      {/* Notificación no invasiva */}
      {notificacion.visible && (
        <div className={`notificacion-toast ${notificacion.tipo}`}>
          {notificacion.mensaje}
        </div>
      )}

      <div className="panel-header-grid">
        <div className="panel-nav-block">
          <h2>CATALOGO</h2>
          <div className="catalogo-tabs catalogo-tabs-catalogo">
            <button
              className={`tab-button ${catalogoSeleccionado === 'MUEBLES' ? 'active' : ''}`}
              onClick={() => setCatalogoSeleccionado('MUEBLES')}
            >
              MUEBLES
            </button>
            <button
              className={`tab-button ${catalogoSeleccionado === 'RESPALDO' ? 'active' : ''}`}
              onClick={() => setCatalogoSeleccionado('RESPALDO')}
            >
              RESPALDOS
            </button>
            <button
              className={`tab-button ${catalogoSeleccionado === 'SILLA' ? 'active' : ''}`}
              onClick={() => setCatalogoSeleccionado('SILLA')}
            >
              SILLAS
            </button>
            <button
              className={`tab-button ${catalogoSeleccionado === 'SOFA' ? 'active' : ''}`}
              onClick={() => setCatalogoSeleccionado('SOFA')}
            >
              SOFAS
            </button>
            <button
              className={`tab-button ${catalogoSeleccionado === 'DECORACION' ? 'active' : ''}`}
              onClick={() => setCatalogoSeleccionado('DECORACION')}
            >
              DECORACIONES
            </button>
            <button
              className={`tab-button ${catalogoSeleccionado === 'ADICIONALES' ? 'active' : ''}`}
              onClick={() => setCatalogoSeleccionado('ADICIONALES')}
            >
              ADICIONALES
            </button>
          </div>
        </div>

        <div className="panel-nav-block panel-nav-tools">
          <h2>HERRAMIENTAS</h2>
          <div className="catalogo-tabs catalogo-tabs-tools">
            <button
              className={`tab-button ${catalogoSeleccionado === 'SUCURSALES' ? 'active' : ''}`}
              onClick={() => setCatalogoSeleccionado('SUCURSALES')}
            >
              SUCURSALES
            </button>
            <button
              className={`tab-button ${catalogoSeleccionado === 'REGISTRO_STOCK' ? 'active' : ''}`}
              onClick={() => setCatalogoSeleccionado('REGISTRO_STOCK')}
            >
              REGISTRO DE STOCK
            </button>
          </div>
        </div>
      </div>

      {catalogoSeleccionado === 'SUCURSALES' ? (
        <>
          <div className="panel-info-box">
            <h3>Configuración de Factores por Sucursal</h3>
            <p>
              Estos coeficientes impactan directamente en el cálculo de precios de presupuestos.
              Los cambios aplican a nuevos presupuestos desde el momento en que se guardan.
            </p>
          </div>
          {renderPanelSucursales()}
        </>
      ) : catalogoSeleccionado === 'REGISTRO_STOCK' ? (
        <>
          {renderPanelRegistroStock()}
        </>
      ) : (
        <>

      {/* Botón para crear producto */}
      <div className="catalogo-actions-row" style={{ marginBottom: '15px', marginTop: '15px' }}>
        <button 
          className="btn-nuevo-producto"
          onClick={abrirModalCrear}
        >
          ➕ Nuevo Producto al Catálogo
        </button>

        {catalogoSeleccionado === 'DECORACION' && (
          <button
            className="btn-actualizacion-global"
            onClick={() => setModalAjusteGlobalAbierto(true)}
          >
            🌐 Actualización Global
          </button>
        )}
        {['RESPALDO', 'SILLA', 'SOFA', 'MUEBLES'].includes(catalogoSeleccionado) && (
          <CatalogoImportacionPreview key={catalogoSeleccionado} categoria={catalogoSeleccionado} onActualizado={() => cargarCatalogo(true)} />
        )}
      </div>

      {/* Botón limpiar filtros */}
      {(filtros.tipo || filtros.modelo || filtros.medidas || filtros.madera || filtros.tapizado || filtros.cantidadTapizado || filtros.sku || filtros.proveedor) && (
        <div style={{ marginBottom: '15px' }}>
          <button 
            className="btn-limpiar-filtros"
            onClick={() => setFiltros({ tipo: '', modelo: '', medidas: '', madera: '', tapizado: '', cantidadTapizado: '' })}
          >
            ✕ Limpiar filtros
          </button>
        </div>
      )}

      <div className="panel-tabla">
        <table>
          <thead>
            <tr>
              {/* TIPO - Ocultar para ADICIONALES */}
              {catalogoSeleccionado !== 'ADICIONALES' && (
                <th>
                  <div className="th-filtro">
                    TIPO
                    <SearchableSelect
                      value={filtros.tipo}
                      onChange={(value) => handleFiltroChange('tipo', value)}
                      options={['', ...opciones.tipos]}
                      placeholder="Todos"
                    />
                  </div>
                </th>
              )}
              <th>
                <div className="th-filtro">
                  {catalogoSeleccionado === 'ADICIONALES' ? 'NOMBRE' : 'MODELO'}
                  <SearchableSelect
                    value={filtros.modelo}
                    onChange={(value) => handleFiltroChange('modelo', value)}
                    options={['', ...opciones.modelos]}
                    placeholder="Todos"
                  />
                </div>
              </th>
              {/* Mostrar SKU solo para DECORACION */}
              {catalogoSeleccionado === 'DECORACION' && (
                <th>
                  <div className="th-filtro">
                    SKU
                    <SearchableSelect
                      value={filtros.sku}
                      onChange={(value) => handleFiltroChange('sku', value)}
                      options={['', ...opciones.skus]}
                      placeholder="Todos"
                    />
                  </div>
                </th>
              )}
              {/* Mostrar PROVEEDOR solo para DECORACION */}
              {catalogoSeleccionado === 'DECORACION' && (
                <th>
                  <div className="th-filtro">
                    PROVEEDOR
                    <SearchableSelect
                      value={filtros.proveedor}
                      onChange={(value) => handleFiltroChange('proveedor', value)}
                      options={['', ...opciones.proveedores]}
                      placeholder="Todos"
                    />
                  </div>
                </th>
              )}
              {/* Mostrar MEDIDAS para MUEBLES, RESPALDO y SOFA (no SILLA, DECORACION ni ADICIONALES) */}
              {catalogoSeleccionado !== 'SILLA' && catalogoSeleccionado !== 'DECORACION' && catalogoSeleccionado !== 'ADICIONALES' && (
                <th>
                  <div className="th-filtro">
                    MEDIDAS
                    <SearchableSelect
                      value={filtros.medidas}
                      onChange={(value) => handleFiltroChange('medidas', value)}
                      options={['', ...opciones.medidas]}
                      placeholder="Todas"
                    />
                  </div>
                </th>
              )}
              {/* Mostrar MADERA solo si NO es SOFA, DECORACION ni ADICIONALES */}
              {catalogoSeleccionado !== 'SOFA' && catalogoSeleccionado !== 'DECORACION' && catalogoSeleccionado !== 'ADICIONALES' && (
                <th>
                  <div className="th-filtro">
                    MADERA
                    <SearchableSelect
                      value={filtros.madera}
                      onChange={(value) => handleFiltroChange('madera', value)}
                      options={['', ...opciones.maderas]}
                      placeholder="Todas"
                    />
                  </div>
                </th>
              )}
              {/* Mostrar TAPIZADO solo si NO es SOFA, DECORACION ni ADICIONALES */}
              {catalogoSeleccionado !== 'SOFA' && catalogoSeleccionado !== 'DECORACION' && catalogoSeleccionado !== 'ADICIONALES' && (
                <th>
                  <div className="th-filtro">
                    TAPIZADO
                    <SearchableSelect
                      value={filtros.tapizado}
                      onChange={(value) => handleFiltroChange('tapizado', value)}
                      options={['', ...opciones.tapizados]}
                      placeholder="Todos"
                    />
                  </div>
                </th>
              )}
              {/* Mostrar CANT TELA/CUERO para RESPALDO, SILLA y SOFA (no ADICIONALES) */}
              {(catalogoSeleccionado === 'RESPALDO' || catalogoSeleccionado === 'SILLA' || catalogoSeleccionado === 'SOFA') && (
                <th>
                  <div className="th-filtro">
                    {catalogoSeleccionado === 'SOFA' ? 'CANT TELA / CUERO' : 'CANTIDAD'}
                    <SearchableSelect
                      value={filtros.cantidadTapizado}
                      onChange={(value) => handleFiltroChange('cantidadTapizado', value)}
                      options={['', ...opciones.cantidadesTapizado]}
                      placeholder="Todas"
                    />
                  </div>
                </th>
              )}
              {/* Columnas de precios */}
              {catalogoSeleccionado === 'DECORACION' ? (
                <th>COSTO</th>
              ) : catalogoSeleccionado === 'ADICIONALES' ? (
                <>
                  <th>TIPO VALOR</th>
                  <th>VALOR</th>
                  <th className="table-center">DECIMALES</th>
                  <th className="table-center">APLICA FACTORES</th>
                </>
              ) : catalogoSeleccionado === 'SOFA' ? (
                <>
                  <th>ECO</th>
                  <th>CUERO</th>
                  <th>TELA</th>
                </>
              ) : (
                <>
                  <th>SIN LUSTRE</th>
                  <th>NATURAL</th>
                  <th>COMUN</th>
                </>
              )}
              <th>ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            {catalogoFiltrado.length === 0 ? (
              <tr>
                <td colSpan={
                  catalogoSeleccionado === 'MUEBLES' ? 9 : 
                  catalogoSeleccionado === 'RESPALDO' ? 9 : 
                  catalogoSeleccionado === 'SILLA' ? 8 : 
                  catalogoSeleccionado === 'SOFA' ? 6 :
                  catalogoSeleccionado === 'DECORACION' ? 6 : 
                  catalogoSeleccionado === 'ADICIONALES' ? 6 : 8
                } className="text-center">
                  {catalogoAgrupado.length === 0 
                    ? 'No hay productos cargados en el sistema'
                    : 'No se encontraron productos con los filtros aplicados'}
                </td>
              </tr>
            ) : (
              catalogoPaginado.map((grupo, index) => (
                <tr key={indiceInicio + index}>
                  {/* TIPO - Ocultar para ADICIONALES */}
                  {catalogoSeleccionado !== 'ADICIONALES' && (
                    <td>{grupo.tipo || '-'}</td>
                  )}
                  <td>{grupo.modelo || '-'}</td>
                  {catalogoSeleccionado === 'DECORACION' && (
                    <>
                      <td>{grupo.sku || '-'}</td>
                      <td>{grupo.proveedor || '-'}</td>
                    </>
                  )}
                  {catalogoSeleccionado !== 'SILLA' && catalogoSeleccionado !== 'DECORACION' && catalogoSeleccionado !== 'ADICIONALES' && (
                    <td>{grupo.medidas || '-'}</td>
                  )}
                  {catalogoSeleccionado !== 'SOFA' && catalogoSeleccionado !== 'DECORACION' && catalogoSeleccionado !== 'ADICIONALES' && (
                    <>
                      <td><strong>{grupo.madera}</strong></td>
                      <td>{grupo.tapizado}</td>
                    </>
                  )}
                  {(catalogoSeleccionado === 'RESPALDO' || catalogoSeleccionado === 'SILLA' || catalogoSeleccionado === 'SOFA') && (
                    <td>{grupo.cantidadTapizado || '-'}</td>
                  )}
                  {catalogoSeleccionado === 'DECORACION' ? (
                    <td className="costo-cell">
                      {tieneCostoActivo(grupo.comun) ? `$${grupo.comun.toLocaleString()}` : '-'}
                    </td>
                  ) : catalogoSeleccionado === 'ADICIONALES' ? (
                    <>
                      <td>{grupo.tipoValor === 'MONETARIO' ? 'MONETARIO' : grupo.tipoValor === 'PORCENTUAL' ? 'PORCENTUAL' : '-'}</td>
                      <td className="costo-cell">
                        {grupo.tipoValor === 'PORCENTUAL' 
                          ? `${grupo.comun || 0}%` 
                          : tieneCostoActivo(grupo.comun)
                            ? `$${grupo.comun.toLocaleString()}` 
                            : '-'}
                      </td>
                      <td className="table-center">
                        <span className={`status-pill ${grupo.aceptaDecimales ? 'enabled' : 'disabled'}`}>
                          <span className="status-pill-icon">{grupo.aceptaDecimales ? '✓' : '✕'}</span>
                          <span>{grupo.aceptaDecimales ? 'Sí' : 'No'}</span>
                        </span>
                      </td>
                      <td className="table-center">
                        <span className={`status-pill ${grupo.aplicaFactores ? 'enabled' : 'disabled'}`}>
                          <span className="status-pill-icon">{grupo.aplicaFactores ? '✓' : '✕'}</span>
                          <span>{grupo.aplicaFactores ? 'Sí' : 'No'}</span>
                        </span>
                      </td>
                    </>
                  ) : catalogoSeleccionado === 'SOFA' ? (
                    <>
                      <td className="costo-cell">
                        {tieneCostoActivo(grupo.eco) ? `$${grupo.eco.toLocaleString()}` : '-'}
                      </td>
                      <td className="costo-cell">
                        {tieneCostoActivo(grupo.cuero) ? `$${grupo.cuero.toLocaleString()}` : '-'}
                      </td>
                      <td className="costo-cell">
                        {tieneCostoActivo(grupo.tela) ? `$${grupo.tela.toLocaleString()}` : '-'}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="costo-cell">
                        {tieneCostoActivo(grupo.sin_lustre) ? `$${grupo.sin_lustre.toLocaleString()}` : '-'}
                      </td>
                      <td className="costo-cell">
                        {tieneCostoActivo(grupo.natural) ? `$${grupo.natural.toLocaleString()}` : '-'}
                      </td>
                      <td className="costo-cell">
                        {tieneCostoActivo(grupo.comun) ? `$${grupo.comun.toLocaleString()}` : '-'}
                      </td>
                    </>
                  )}
                  <td>
                    {/* <button className="btn-edit" title="Editar nombre del producto" aria-label={`Editar nombre de ${grupo.modelo}`} disabled={renombrando}
                      onClick={() => handleEditarNombre(grupo)}>
                      ✎
                    </button> */}
                    <button 
                      className="btn-edit" 
                      title={['DECORACION', 'ADICIONALES'].includes(grupo.categoria) ? 'Editar costos' : 'Editar variantes'}
                      onClick={() => handleEditarCosto(grupo)}
                    >
                      ✏️
                    </button>
                    <button 
                      className="btn-delete" 
                      title="Eliminar estas variantes del catálogo"
                      onClick={() => handleEliminarProducto(
                        grupo.catalogoId, 
                        grupo.tipo, 
                        grupo.modelo, 
                        grupo.grupoMadera, // ✅ Usar grupoMadera (grupoActualizacion completo)
                        grupo.tapizado, 
                        grupo.cantidadTapizado, 
                        grupo.medidas, 
                        grupo.categoria,
                        grupo.sku,
                        grupo.proveedor
                      )}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Controles de paginación */}
      {catalogoFiltrado.length > 0 && (
        <div className="paginacion-container">
          <div className="paginacion-info">
            Mostrando {indiceInicio + 1} - {Math.min(indiceFin, totalItems)} de {totalItems} productos
          </div>
          
          <div className="paginacion-controles">
            <button 
              onClick={() => irAPagina(1)}
              disabled={paginaActual === 1}
              className="btn-paginacion"
              title="Primera página"
            >
              ««
            </button>
            
            <button 
              onClick={() => irAPagina(paginaActual - 1)}
              disabled={paginaActual === 1}
              className="btn-paginacion"
              title="Página anterior"
            >
              ‹
            </button>
            
            <div className="paginacion-paginas">
              {Array.from({ length: totalPaginas }, (_, i) => i + 1)
                .filter(pagina => {
                  // Mostrar solo páginas cercanas a la actual
                  return pagina === 1 || 
                         pagina === totalPaginas || 
                         (pagina >= paginaActual - 2 && pagina <= paginaActual + 2);
                })
                .map((pagina, index, array) => (
                  <React.Fragment key={pagina}>
                    {index > 0 && array[index - 1] !== pagina - 1 && (
                      <span className="paginacion-ellipsis">...</span>
                    )}
                    <button
                      onClick={() => irAPagina(pagina)}
                      className={`btn-paginacion ${paginaActual === pagina ? 'active' : ''}`}
                    >
                      {pagina}
                    </button>
                  </React.Fragment>
                ))
              }
            </div>
            
            <button 
              onClick={() => irAPagina(paginaActual + 1)}
              disabled={paginaActual === totalPaginas}
              className="btn-paginacion"
              title="Página siguiente"
            >
              ›
            </button>
            
            <button 
              onClick={() => irAPagina(totalPaginas)}
              disabled={paginaActual === totalPaginas}
              className="btn-paginacion"
              title="Última página"
            >
              »»
            </button>
          </div>

          <div className="paginacion-selector">
            <label>Items por página:</label>
            <select 
              value={itemsPorPagina} 
              onChange={(e) => {
                setItemsPorPagina(Number(e.target.value));
                setPaginaActual(1);
              }}
              className="select-items-pagina"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      )}
        </>
      )}

      {/* Modal de edición */}
      {modalAjusteGlobalAbierto && (
        <div className="modal-overlay" onClick={() => setModalAjusteGlobalAbierto(false)}>
          <div className="modal-content modal-ajuste-decoracion" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Actualización Global - Decoraciones</h2>
              <button className="modal-close" onClick={() => setModalAjusteGlobalAbierto(false)}>×</button>
            </div>

            <div className="modal-body modal-ajuste-body">
              {(cargandoPreviewAjusteDecoracion || aplicandoAjusteDecoracion) && (
                <div className="ajuste-procesando-overlay">
                  <div className="ajuste-spinner" />
                  <span>{cargandoPreviewAjusteDecoracion ? 'Calculando vista previa...' : 'Aplicando ajuste global...'}</span>
                </div>
              )}

              <div className="ajuste-decoracion-card ajuste-decoracion-modal-card">
                <div className="ajuste-decoracion-controls">
                  <div className="form-group ajuste-decoracion-form">
                    <label>PROVEEDOR</label>
                    <SearchableSelect
                      value={ajusteDecoracion.proveedor}
                      onChange={(value) => handleAjusteDecoracionChange('proveedor', value)}
                      options={proveedoresDecoracion}
                      placeholder="Seleccionar proveedor"
                      allowCustom={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>AJUSTE (%)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={ajusteDecoracion.porcentaje}
                      onChange={(e) => handlePorcentajeAjusteChange(e.target.value)}
                      placeholder="Ej: +12 o -5.5"
                    />
                  </div>

                  <div className="ajuste-decoracion-actions">
                    <button
                      className="btn-preview-ajuste"
                      onClick={handlePreviewAjusteDecoracion}
                      disabled={cargandoPreviewAjusteDecoracion || aplicandoAjusteDecoracion}
                    >
                      {cargandoPreviewAjusteDecoracion ? 'Calculando...' : '🔎 Vista previa'}
                    </button>

                    <button
                      className="btn-aplicar-ajuste"
                      onClick={handleAplicarAjusteDecoracion}
                      disabled={aplicandoAjusteDecoracion || cargandoPreviewAjusteDecoracion || !previewAjusteDecoracion}
                    >
                      {aplicandoAjusteDecoracion ? 'Aplicando...' : '✅ Aplicar ajuste'}
                    </button>
                  </div>
                </div>

                <div className="ajuste-decoracion-preview-shell">
                  {previewAjusteDecoracion ? (
                    <div className="ajuste-decoracion-preview">
                      <div className="preview-resumen-grid">
                        <div><strong>Proveedor:</strong> {previewAjusteDecoracion.proveedor}</div>
                        <div><strong>Ajuste:</strong> {previewAjusteDecoracion.porcentaje > 0 ? '+' : ''}{previewAjusteDecoracion.porcentaje}%</div>
                        <div><strong>Decoraciones afectadas:</strong> {previewAjusteDecoracion.totalVariantes}</div>
                        <div><strong>Diferencia total:</strong> {previewAjusteDecoracion.diferenciaTotal >= 0 ? '+' : ''}${previewAjusteDecoracion.diferenciaTotal.toLocaleString()}</div>
                      </div>

                      <div className="preview-totales">
                        <span>Total actual: <strong>${previewAjusteDecoracion.totalActual.toLocaleString()}</strong></span>
                        <span>Total nuevo: <strong>${previewAjusteDecoracion.totalNuevo.toLocaleString()}</strong></span>
                      </div>

                      <div className="preview-tabla-wrap">
                        <table className="preview-tabla">
                          <thead>
                            <tr>
                              <th>MODELO</th>
                              <th>SKU</th>
                              <th>ACTUAL</th>
                              <th>NUEVO</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewAjusteDecoracion.muestra.map((fila, idx) => (
                              <tr key={`${fila.sku}-${idx}`}>
                                <td>{fila.modelo || '-'}</td>
                                <td>{fila.sku || '-'}</td>
                                <td>${fila.costoActual.toLocaleString()}</td>
                                <td>${fila.costoNuevo.toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="preview-empty-state">
                      <h4>Vista previa del ajuste</h4>
                      <p>Acá vas a ver el impacto antes de aplicar cambios. Seleccioná proveedor, cargá porcentaje y presioná Vista previa.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edición */}
      {editando?.modo === 'VARIANTES' && (
        <CatalogoVariantesEditor producto={editando} onCancelar={handleCancelar} onGuardado={async () => {
          await cargarCatalogo(true);
          setEditando(null);
          mostrarNotificacion('Variantes actualizadas correctamente', 'success');
        }} />
      )}
      {editando && editando.modo !== 'VARIANTES' && (
        <div className="modal-overlay" onClick={handleCancelar}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Editar Costos</h2>
              <button className="modal-close" onClick={handleCancelar}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="producto-info">
                <h3>{editando.tipo} {editando.modelo}</h3>
                {editando.categoria === 'ADICIONALES' ? (
                  <p><strong>Nombre:</strong> {editando.modelo}</p>
                ) : editando.categoria === 'DECORACION' ? (
                  <>
                    <p><strong>SKU:</strong> {editando.sku}</p>
                    <p><strong>Proveedor:</strong> {editando.proveedor}</p>
                  </>
                ) : editando.categoria === 'SOFA' ? (
                  <>
                    <p><strong>Medidas:</strong> {editando.medidas}</p>
                    <p><strong>Cantidad Tela/Cuero:</strong> {editando.cantidadTapizado}</p>
                  </>
                ) : (
                  <>
                    <p><strong>Medidas:</strong> {editando.medidas}</p>
                    <p><strong>Madera:</strong> {editando.madera || editando.grupoMadera}</p>
                    {editando.tapizado && <p><strong>Tapizado:</strong> {editando.tapizado}</p>}
                    {editando.cantidadTapizado && <p><strong>Cantidad:</strong> {editando.cantidadTapizado}</p>}
                  </>
                )}
              </div>

              <div className="costos-form">
                {editando.categoria === 'ADICIONALES' ? (
                  <>
                    <div className="form-group">
                      <label>TIPO DE VALOR</label>
                      <select
                        value={editando.tipoValor}
                        onChange={(e) => setEditando({
                          ...editando,
                          tipoValor: e.target.value
                        })}
                      >
                        <option value="MONETARIO">MONETARIO</option>
                        <option value="PORCENTUAL">PORCENTUAL</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>VALOR</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={editando.costos.comun}
                        onChange={(e) => {
                          const valor = e.target.value.replace(/[^0-9]/g, '');
                          setEditando({
                            ...editando,
                            costos: { ...editando.costos, comun: valor === '' ? '' : parseInt(valor) }
                          });
                        }}
                        placeholder="0"
                      />
                    </div>

                    <div className="adicional-settings-group">
                      <label className="adicional-setting-card">
                        <input
                          type="checkbox"
                          checked={editando.aceptaDecimales}
                          onChange={(e) => setEditando({
                            ...editando,
                            aceptaDecimales: e.target.checked
                          })}
                        />
                        <span className="adicional-setting-content">
                          <span className="adicional-setting-title">Acepta cantidades decimales</span>
                          <span className="adicional-setting-help">Permite valores como 0.8 o 0,8 al presupuestar.</span>
                        </span>
                      </label>

                      <label className="adicional-setting-card">
                        <input
                          type="checkbox"
                          checked={editando.aplicaFactores}
                          onChange={(e) => setEditando({
                            ...editando,
                            aplicaFactores: e.target.checked
                          })}
                        />
                        <span className="adicional-setting-content">
                          <span className="adicional-setting-title">Aplica factores</span>
                          <span className="adicional-setting-help">Ajusta el valor segun la sucursal del presupuesto.</span>
                        </span>
                      </label>
                    </div>
                  </>
                ) : editando.categoria === 'DECORACION' ? (
                  <div className="form-group">
                    <label>COSTO</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={editando.costos.comun}
                      onChange={(e) => {
                        const valor = e.target.value.replace(/[^0-9]/g, '');
                        setEditando({
                          ...editando,
                          costos: { ...editando.costos, comun: valor === '' ? '' : parseInt(valor) }
                        });
                      }}
                      placeholder="0"
                    />
                  </div>
                ) : editando.categoria === 'SOFA' ? (
                  <>
                    <div className="form-group">
                      <label>ECO</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={editando.costos.sin_lustre}
                        onChange={(e) => {
                          const valor = e.target.value.replace(/[^0-9]/g, '');
                          setEditando({
                            ...editando,
                            costos: { ...editando.costos, sin_lustre: valor === '' ? '' : parseInt(valor) }
                          });
                        }}
                        placeholder="0"
                      />
                    </div>

                    <div className="form-group">
                      <label>CUERO</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={editando.costos.natural}
                        onChange={(e) => {
                          const valor = e.target.value.replace(/[^0-9]/g, '');
                          setEditando({
                            ...editando,
                            costos: { ...editando.costos, natural: valor === '' ? '' : parseInt(valor) }
                          });
                        }}
                        placeholder="0"
                      />
                    </div>

                    <div className="form-group">
                      <label>TELA</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={editando.costos.comun}
                        onChange={(e) => {
                          const valor = e.target.value.replace(/[^0-9]/g, '');
                          setEditando({
                            ...editando,
                            costos: { ...editando.costos, comun: valor === '' ? '' : parseInt(valor) }
                          });
                        }}
                        placeholder="0"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label>SIN LUSTRE</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={editando.costos.sin_lustre}
                        onChange={(e) => {
                          const valor = e.target.value.replace(/[^0-9]/g, '');
                          setEditando({
                            ...editando,
                            costos: { ...editando.costos, sin_lustre: valor === '' ? '' : parseInt(valor) }
                          });
                        }}
                        placeholder="0"
                      />
                    </div>

                    <div className="form-group">
                      <label>NATURAL</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={editando.costos.natural}
                        onChange={(e) => {
                          const valor = e.target.value.replace(/[^0-9]/g, '');
                          setEditando({
                            ...editando,
                            costos: { ...editando.costos, natural: valor === '' ? '' : parseInt(valor) }
                          });
                        }}
                        placeholder="0"
                      />
                    </div>

                    <div className="form-group">
                      <label>COMUN</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={editando.costos.comun}
                        onChange={(e) => {
                          const valor = e.target.value.replace(/[^0-9]/g, '');
                          setEditando({
                            ...editando,
                            costos: { ...editando.costos, comun: valor === '' ? '' : parseInt(valor) }
                          });
                        }}
                        placeholder="0"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn-guardar" 
                onClick={handleGuardarCostos}
                disabled={guardando}
              >
                {guardando ? 'Guardando...' : '💾 Guardar'}
              </button>
              <button className="btn-cancelar" onClick={handleCancelar}>
                ✕ Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de creación */}
      {modalCrearAbierto && (
        <div className="modal-overlay" onClick={cerrarModalCrear}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Crear Producto - {catalogoSeleccionado}</h2>
              <button className="modal-close" onClick={cerrarModalCrear}>×</button>
            </div>
            
            <div className="modal-body">
              {catalogoSeleccionado === 'DECORACION' ? (
                /* Formulario DECORACION */
                <>
                  <div className="form-group">
                    <label>MODELO *</label>
                    <SearchableSelect
                      value={nuevoProducto.modelo}
                      onChange={(value) => setNuevoProducto({
                        ...nuevoProducto,
                        modelo: value
                      })}
                      options={opcionesCrear.modelos}
                      placeholder="Seleccionar o escribir modelo"
                      allowCustom={true}
                      disabled={creando}
                    />
                  </div>

                  <div className="form-group">
                    <label>SKU (Opcional - se auto-genera si está vacío)</label>
                    <input
                      type="text"
                      value={nuevoProducto.sku}
                      onChange={(e) => setNuevoProducto({
                        ...nuevoProducto,
                        sku: e.target.value
                      })}
                      placeholder="Dejar vacío para auto-generar"
                      disabled={creando}
                    />
                  </div>

                  <div className="form-group">
                    <label>PROVEEDOR *</label>
                    <SearchableSelect
                      value={nuevoProducto.proveedor}
                      onChange={(value) => setNuevoProducto({
                        ...nuevoProducto,
                        proveedor: value
                      })}
                      options={opcionesCrear.proveedores}
                      placeholder="Seleccionar o escribir proveedor"
                      allowCustom={true}
                      disabled={creando}
                    />
                  </div>

                  <div className="form-group">
                    <label>COSTO *</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={nuevoProducto.lustres.sinLustre.costo}
                      onChange={(e) => {
                        const valor = e.target.value.replace(/[^0-9]/g, '');
                        setNuevoProducto({
                          ...nuevoProducto,
                          lustres: {
                            ...nuevoProducto.lustres,
                            sinLustre: { seleccionado: true, costo: valor }
                          }
                        });
                      }}
                      placeholder="Ej: 56000"
                      disabled={creando}
                    />
                  </div>
                </>
              ) : catalogoSeleccionado === 'ADICIONALES' ? (
                /* Formulario ADICIONALES */
                <>
                  <div className="form-group">
                    <label>NOMBRE DEL ADICIONAL *</label>
                    <SearchableSelect
                      value={nuevoProducto.modelo}
                      onChange={(value) => setNuevoProducto({
                        ...nuevoProducto,
                        modelo: value
                      })}
                      options={opcionesCrear.modelos}
                      placeholder="Seleccionar o escribir adicional"
                      allowCustom={true}
                      disabled={creando}
                    />
                  </div>

                  <div className="form-group">
                    <label>TIPO DE VALOR *</label>
                    <div className="radio-group" style={{ display: 'flex', gap: '30px', marginTop: '10px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
                        <input
                          type="radio"
                          name="tipoValor"
                          value="MONETARIO"
                          checked={nuevoProducto.tipoValor === 'MONETARIO'}
                          onChange={(e) => setNuevoProducto({
                            ...nuevoProducto,
                            tipoValor: e.target.value
                          })}
                          disabled={creando}
                          style={{ marginRight: '8px' }}
                        />
                        Monetario ($)
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
                        <input
                          type="radio"
                          name="tipoValor"
                          value="PORCENTUAL"
                          checked={nuevoProducto.tipoValor === 'PORCENTUAL'}
                          onChange={(e) => setNuevoProducto({
                            ...nuevoProducto,
                            tipoValor: e.target.value
                          })}
                          disabled={creando}
                          style={{ marginRight: '8px' }}
                        />
                        Porcentual (%)
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>VALOR ($) *</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={nuevoProducto.valor}
                      onChange={(e) => {
                        const valor = e.target.value.replace(/[^0-9]/g, '');
                        setNuevoProducto({
                          ...nuevoProducto,
                          valor: valor
                        });
                      }}
                      placeholder={nuevoProducto.tipoValor === 'MONETARIO' ? 'Ej: 40000' : 'Ej: 15'}
                      disabled={creando}
                    />
                  </div>

                  <div className="adicional-settings-group adicional-settings-group-create">
                    <label className="adicional-setting-card">
                      <input
                        type="checkbox"
                        checked={nuevoProducto.aceptaDecimales}
                        onChange={(e) => setNuevoProducto({
                          ...nuevoProducto,
                          aceptaDecimales: e.target.checked
                        })}
                        disabled={creando}
                      />
                      <span className="adicional-setting-content">
                        <span className="adicional-setting-title">Aceptar cantidades decimales</span>
                        <span className="adicional-setting-help">
                          {nuevoProducto.aceptaDecimales
                            ? 'En presupuestos permitira cantidades con decimales, por ejemplo 0.8 o 0,8.'
                            : 'En presupuestos solo permitira cantidades enteras.'}
                        </span>
                      </span>
                    </label>

                    <label className="adicional-setting-card">
                      <input
                        type="checkbox"
                        checked={nuevoProducto.aplicaFactores}
                        onChange={(e) => setNuevoProducto({
                          ...nuevoProducto,
                          aplicaFactores: e.target.checked
                        })}
                        disabled={creando}
                      />
                      <span className="adicional-setting-content">
                        <span className="adicional-setting-title">Aplicar factores de ganancia por sucursal</span>
                        <span className="adicional-setting-help">
                          {nuevoProducto.aplicaFactores
                            ? 'El valor se ajustara segun la sucursal.'
                            : 'El valor sera fijo sin importar la sucursal.'}
                        </span>
                      </span>
                    </label>
                  </div>
                </>
              ) : (
                /* Formulario MUEBLES, SILLA, SOFA, RESPALDO */
                <>
                  <div className="form-group">
                    <label>TIPO *</label>
                    <SearchableSelect
                      value={nuevoProducto.tipo}
                      onChange={(value) => setNuevoProducto({
                        ...nuevoProducto,
                        tipo: value
                      })}
                      options={opcionesCrear.tipos}
                      placeholder="Seleccionar o escribir tipo"
                      allowCustom={true}
                      disabled={creando}
                    />
                  </div>

                  <div className="form-group">
                    <label>MODELO *</label>
                    <SearchableSelect
                      value={nuevoProducto.modelo}
                      onChange={(value) => setNuevoProducto({
                        ...nuevoProducto,
                        modelo: value
                      })}
                      options={opcionesCrear.modelos}
                      placeholder="Seleccionar o escribir modelo"
                      allowCustom={true}
                      disabled={creando}
                    />
                  </div>

                  {catalogoSeleccionado !== 'SILLA' && (
                    <div className="form-group">
                      <label>MEDIDAS *</label>
                      <SearchableSelect
                        value={nuevoProducto.medidas}
                        onChange={(value) => setNuevoProducto({
                          ...nuevoProducto,
                          medidas: value
                        })}
                        options={opcionesCrear.medidas}
                        placeholder="Seleccionar o escribir medidas"
                        allowCustom={true}
                        disabled={creando}
                      />
                    </div>
                  )}

                  {catalogoSeleccionado !== 'SOFA' && (
                    <div className="form-group">
                      <label>MADERA *</label>
                      <SearchableSelect
                        value={nuevoProducto.madera}
                        onChange={(value) => setNuevoProducto({
                          ...nuevoProducto,
                          madera: value
                        })}
                        options={opcionesCrear.maderas}
                        placeholder="Seleccionar o escribir madera"
                        allowCustom={true}
                        disabled={creando}
                      />
                    </div>
                  )}

                  {catalogoSeleccionado === 'SOFA' && (
                    <div className="form-group">
                      <label>CANT TELA/CUERO *</label>
                      <SearchableSelect
                        value={nuevoProducto.cantidadTapizado}
                        onChange={(value) => setNuevoProducto({
                          ...nuevoProducto,
                          cantidadTapizado: value
                        })}
                        options={opcionesCrear.cantidadesTapizado}
                        placeholder="Seleccionar o escribir cantidad"
                        allowCustom={true}
                        disabled={creando}
                      />
                    </div>
                  )}

                  {(catalogoSeleccionado === 'RESPALDO' || catalogoSeleccionado === 'SILLA') && (
                    <div className="form-group">
                      <label>TAPIZADO</label>
                      <SearchableSelect
                        value={nuevoProducto.tapizado}
                        onChange={(value) => setNuevoProducto({
                          ...nuevoProducto,
                          tapizado: value
                        })}
                        options={opcionesCrear.tapizados}
                        placeholder="Seleccionar o escribir tapizado"
                        allowCustom={true}
                        disabled={creando}
                      />
                    </div>
                  )}

                  {(catalogoSeleccionado === 'RESPALDO' || catalogoSeleccionado === 'SILLA') && (
                    <div className="form-group">
                      <label>CANTIDAD TAPIZADO</label>
                      <SearchableSelect
                        value={nuevoProducto.cantidadTapizado}
                        onChange={(value) => setNuevoProducto({
                          ...nuevoProducto,
                          cantidadTapizado: value
                        })}
                        options={opcionesCrear.cantidadesTapizado}
                        placeholder="Seleccionar o escribir cantidad"
                        allowCustom={true}
                        disabled={creando}
                      />
                    </div>
                  )}

                  {/* Checkboxes de lustres/materiales con inputs de costo */}
                  <div className="form-group">
                    <label>{catalogoSeleccionado === 'SOFA' ? 'Materiales y Costos *' : 'Lustres y Costos *'}</label>
                    <div className="lustres-checkboxes">
                      {catalogoSeleccionado === 'SOFA' ? (
                        <>
                          <div className="checkbox-grupo">
                            <label>
                              <input
                                type="checkbox"
                                checked={nuevoProducto.lustres.eco.seleccionado}
                                onChange={(e) => setNuevoProducto({
                                  ...nuevoProducto,
                                  lustres: {
                                    ...nuevoProducto.lustres,
                                    eco: { ...nuevoProducto.lustres.eco, seleccionado: e.target.checked }
                                  }
                                })}
                                disabled={creando}
                              />
                              ECO
                            </label>
                            {nuevoProducto.lustres.eco.seleccionado && (
                              <input
                                type="text"
                                inputMode="numeric"
                                value={nuevoProducto.lustres.eco.costo}
                                onChange={(e) => {
                                  const valor = e.target.value.replace(/[^0-9]/g, '');
                                  setNuevoProducto({
                                    ...nuevoProducto,
                                    lustres: {
                                      ...nuevoProducto.lustres,
                                      eco: { ...nuevoProducto.lustres.eco, costo: valor }
                                    }
                                  });
                                }}
                                placeholder="Costo"
                                disabled={creando}
                              />
                            )}
                          </div>

                          <div className="checkbox-grupo">
                            <label>
                              <input
                                type="checkbox"
                                checked={nuevoProducto.lustres.cuero.seleccionado}
                                onChange={(e) => setNuevoProducto({
                                  ...nuevoProducto,
                                  lustres: {
                                    ...nuevoProducto.lustres,
                                    cuero: { ...nuevoProducto.lustres.cuero, seleccionado: e.target.checked }
                                  }
                                })}
                                disabled={creando}
                              />
                              CUERO
                            </label>
                            {nuevoProducto.lustres.cuero.seleccionado && (
                              <input
                                type="text"
                                inputMode="numeric"
                                value={nuevoProducto.lustres.cuero.costo}
                                onChange={(e) => {
                                  const valor = e.target.value.replace(/[^0-9]/g, '');
                                  setNuevoProducto({
                                    ...nuevoProducto,
                                    lustres: {
                                      ...nuevoProducto.lustres,
                                      cuero: { ...nuevoProducto.lustres.cuero, costo: valor }
                                    }
                                  });
                                }}
                                placeholder="Costo"
                                disabled={creando}
                              />
                            )}
                          </div>

                          <div className="checkbox-grupo">
                            <label>
                              <input
                                type="checkbox"
                                checked={nuevoProducto.lustres.tela.seleccionado}
                                onChange={(e) => setNuevoProducto({
                                  ...nuevoProducto,
                                  lustres: {
                                    ...nuevoProducto.lustres,
                                    tela: { ...nuevoProducto.lustres.tela, seleccionado: e.target.checked }
                                  }
                                })}
                                disabled={creando}
                              />
                              TELA
                            </label>
                            {nuevoProducto.lustres.tela.seleccionado && (
                              <input
                                type="text"
                                inputMode="numeric"
                                value={nuevoProducto.lustres.tela.costo}
                                onChange={(e) => {
                                  const valor = e.target.value.replace(/[^0-9]/g, '');
                                  setNuevoProducto({
                                    ...nuevoProducto,
                                    lustres: {
                                      ...nuevoProducto.lustres,
                                      tela: { ...nuevoProducto.lustres.tela, costo: valor }
                                    }
                                  });
                                }}
                                placeholder="Costo"
                                disabled={creando}
                              />
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="checkbox-grupo">
                            <label>
                              <input
                                type="checkbox"
                                checked={nuevoProducto.lustres.sinLustre.seleccionado}
                                onChange={(e) => setNuevoProducto({
                                  ...nuevoProducto,
                                  lustres: {
                                    ...nuevoProducto.lustres,
                                    sinLustre: { ...nuevoProducto.lustres.sinLustre, seleccionado: e.target.checked }
                                  }
                                })}
                                disabled={creando}
                              />
                              SIN LUSTRE
                            </label>
                            {nuevoProducto.lustres.sinLustre.seleccionado && (
                              <input
                                type="text"
                                inputMode="numeric"
                                value={nuevoProducto.lustres.sinLustre.costo}
                                onChange={(e) => {
                                  const valor = e.target.value.replace(/[^0-9]/g, '');
                                  setNuevoProducto({
                                    ...nuevoProducto,
                                    lustres: {
                                      ...nuevoProducto.lustres,
                                      sinLustre: { ...nuevoProducto.lustres.sinLustre, costo: valor }
                                    }
                                  });
                                }}
                                placeholder="Costo"
                                disabled={creando}
                              />
                            )}
                          </div>

                          <div className="checkbox-grupo">
                            <label>
                              <input
                                type="checkbox"
                                checked={nuevoProducto.lustres.natural.seleccionado}
                                onChange={(e) => setNuevoProducto({
                                  ...nuevoProducto,
                                  lustres: {
                                    ...nuevoProducto.lustres,
                                    natural: { ...nuevoProducto.lustres.natural, seleccionado: e.target.checked }
                                  }
                                })}
                                disabled={creando}
                              />
                              NATURAL
                            </label>
                            {nuevoProducto.lustres.natural.seleccionado && (
                              <input
                                type="text"
                                inputMode="numeric"
                                value={nuevoProducto.lustres.natural.costo}
                                onChange={(e) => {
                                  const valor = e.target.value.replace(/[^0-9]/g, '');
                                  setNuevoProducto({
                                    ...nuevoProducto,
                                    lustres: {
                                      ...nuevoProducto.lustres,
                                      natural: { ...nuevoProducto.lustres.natural, costo: valor }
                                    }
                                  });
                                }}
                                placeholder="Costo"
                                disabled={creando}
                              />
                            )}
                          </div>

                          <div className="checkbox-grupo">
                            <label>
                              <input
                                type="checkbox"
                                checked={nuevoProducto.lustres.comun.seleccionado}
                                onChange={(e) => setNuevoProducto({
                                  ...nuevoProducto,
                                  lustres: {
                                    ...nuevoProducto.lustres,
                                    comun: { ...nuevoProducto.lustres.comun, seleccionado: e.target.checked }
                                  }
                                })}
                                disabled={creando}
                              />
                              COMUN
                            </label>
                            {nuevoProducto.lustres.comun.seleccionado && (
                              <input
                                type="text"
                                inputMode="numeric"
                                value={nuevoProducto.lustres.comun.costo}
                                onChange={(e) => {
                                  const valor = e.target.value.replace(/[^0-9]/g, '');
                                  setNuevoProducto({
                                    ...nuevoProducto,
                                    lustres: {
                                      ...nuevoProducto.lustres,
                                      comun: { ...nuevoProducto.lustres.comun, costo: valor }
                                    }
                                  });
                                }}
                                placeholder="Costo"
                                disabled={creando}
                              />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="btn-guardar" 
                onClick={handleCrearProducto}
                disabled={creando}
              >
                {creando ? 'Creando...' : '✅ Crear Producto'}
              </button>
              <button className="btn-cancelar" onClick={cerrarModalCrear}>
                ✕ Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PanelControl;
